/**
 * PreferencesBar — global theme + mute controls (DESKTOP presentation).
 *
 * Mounted globally in `App.tsx`. Floats top-right, never overlaps the page's
 * own header (uses a fixed position with a high z-index and a small inset).
 * The control surface uses the Reading Room aesthetic by default; variants
 * may restyle via the `.prefs-bar` selectors if they want a different look
 * (each variant gets a `data-variant` attribute on `<html>` later if needed).
 *
 * Below 768px the inline pill cluster crowds the content surface — that
 * viewport is served by <PreferencesPopout /> (a designed cream-paper
 * popout from a single gear-icon trigger). This component returns null
 * there so the two presentations don't double-up.
 *
 * Accessibility:
 *  - Each toggle is a `<button aria-pressed>` so screen readers announce
 *    "Daylight, pressed" / "Mute, not pressed."
 *  - Icons paired with text — color never carries information on its own.
 *  - Tap targets are 44px square minimum at typical sizing.
 */
import { useIsMobileChrome } from './useIsMobileChrome';
import { usePreferences } from './usePreferences';

import './PreferencesBar.css';

export function PreferencesBar() {
  const { theme, setTheme, muted, toggleMute } = usePreferences();
  const isMobile = useIsMobileChrome();

  // Mobile presentation lives in <PreferencesPopout />.
  if (isMobile) return null;

  return (
    <div
      className="prefs-bar"
      role="group"
      aria-label="Game preferences"
    >
      {/* Theme group — daylight / lamplight */}
      <div className="prefs-bar__group" role="group" aria-label="Choose room light">
        <button
          type="button"
          className={`prefs-bar__btn${theme === 'light' ? ' is-on' : ''}`}
          onClick={() => setTheme('light')}
          aria-pressed={theme === 'light'}
          title="Daylight"
        >
          <SunIcon />
          <span className="prefs-bar__btn-label">Daylight</span>
        </button>
        <button
          type="button"
          className={`prefs-bar__btn${theme === 'dark' ? ' is-on' : ''}`}
          onClick={() => setTheme('dark')}
          aria-pressed={theme === 'dark'}
          title="Lamplight"
        >
          <MoonIcon />
          <span className="prefs-bar__btn-label">Lamplight</span>
        </button>
      </div>

      {/* Mute toggle */}
      <button
        type="button"
        className={`prefs-bar__btn prefs-bar__btn--mute${muted ? ' is-muted' : ''}`}
        onClick={toggleMute}
        aria-pressed={muted}
        title={muted ? 'Sound is off — tap to turn on' : 'Sound is on — tap to mute'}
      >
        {muted ? <SpeakerMutedIcon /> : <SpeakerIcon />}
        <span className="prefs-bar__btn-label">{muted ? 'Muted' : 'Sound'}</span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function SunIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2 2M17.1 17.1l2 2M4.9 19.1l2-2M17.1 6.9l2-2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 9v6h4l5 4V5L8 9H4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16 8.5c1.2 1 2 2.3 2 3.5s-0.8 2.5-2 3.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M19 6c2 1.5 3 3.7 3 6s-1 4.5-3 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        opacity="0.55"
      />
    </svg>
  );
}

function SpeakerMutedIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 9v6h4l5 4V5L8 9H4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16 9l5 5M21 9l-5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
